import { Injectable } from '@angular/core';
import { mergeWith } from 'lodash';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import { AutosaveFormDetailsParams, AutosaveQueueValidationStatus, AutosaveValidationData, AutosaveValidationInputData, BusinessInformationFormData, OwnerInformationFormData, OwnersDiffData, SubmissionFormData, SubmissionFormDiffData } from '../../form-details.model';
import { diff as deepDiff } from 'deep-object-diff';
import { CommonHelper, DeepIsEmptyOptions } from 'common';

@Injectable({
  providedIn: 'root'
})
export class AutosaveValidationService {

  validateAutosaveStatus(inputData: AutosaveValidationInputData): AutosaveValidationData {
    const isAutosaveBlocked = this.checkIfIsAutosaveBlocked(inputData.autosaveParams, inputData.submissionForm);
    const effectivePayload = this.getEffectivePayload(inputData.submissionForm, inputData.lastSavedEffectivePayload);
    const diffPayload = this.getDiffPayload(inputData.lastSavedEffectivePayload, effectivePayload);
    const queueValidationData = this.validateAutosaveQueue(diffPayload, inputData.pendingDiffPayload, inputData.processingDiffPayload);
    return {
      ...queueValidationData,
      isAutosaveBlocked,
      effectivePayload,
      diffPayload
    };
  }

  checkIfIsAutosaveBlocked(autosaveParams: AutosaveFormDetailsParams, submissionForm: FormGroup): boolean {
    return (!autosaveParams?.accountId || !autosaveParams?.applicationId) && submissionForm.get('businessInformation.name')?.invalid;
  }

  getDiffPayload(
    lastSavedData: SubmissionFormData,
    effectivePayload: SubmissionFormData
  ): SubmissionFormDiffData {
    const businessDiffData: BusinessInformationFormData = deepDiff(
      lastSavedData?.businessInformation || {},
      effectivePayload?.businessInformation || {}
    );
    const currentOwners = effectivePayload?.ownersInformation || [];
    const lastSavedOwners = lastSavedData?.ownersInformation || [];
    const ownersDiffData = this.getOwnersDiff(currentOwners, lastSavedOwners);

    const ownersEmpty =
      (!ownersDiffData.addedOrUpdated || ownersDiffData.addedOrUpdated.length === 0) &&
      (!ownersDiffData.removed || ownersDiffData.removed.length === 0);

    const result: SubmissionFormDiffData = {};
    if (!this.isEmpty(businessDiffData)) {
      result.businessDiffData = businessDiffData;
    }
    if (!ownersEmpty) {
      result.ownersDiffData = ownersDiffData;
    }
    return result;
  }

  private getEffectivePayload(submissionForm: FormGroup, lastSavedData: SubmissionFormData): SubmissionFormData {
    const allowEmptyString = !this.isEmpty(lastSavedData);
    const savableData: SubmissionFormData = this.extractSavableData(submissionForm, allowEmptyString);
    return this.mergePayloads(lastSavedData, savableData);
  }

  private validateAutosaveQueue(diffPayload: SubmissionFormDiffData, pendingDiffPayload: SubmissionFormDiffData, processingDiffPayload: SubmissionFormDiffData): AutosaveQueueValidationStatus {
    if (this.isEmpty(diffPayload)) {
      return { hasAnySavableData: false };
    }
    if (pendingDiffPayload) {
      if (this.isEqual(pendingDiffPayload, diffPayload)) {
        return { hasAnySavableData: false };
      }
      if (this.isEqual(processingDiffPayload, diffPayload)) {
        return { hasAnySavableData: false, canClearPendingPayload: true };
      }
    }
    if (!pendingDiffPayload && processingDiffPayload && this.isEqual(processingDiffPayload, diffPayload)) {
      return { hasAnySavableData: false };
    }
    return { hasAnySavableData: true };
  }

  private extractSavableData(control: AbstractControl, allowEmptyString: boolean, controlKey?: string): any {
    if (control instanceof FormControl) {
      return this.extractFormControlData(control, allowEmptyString, controlKey);
    } else if (control instanceof FormGroup) {
      return this.extractFormGroupData(control, allowEmptyString);
    } else if (control instanceof FormArray) {
      return this.extractFormArrayData(control, allowEmptyString);
    }
    return undefined;
  }

  private extractFormControlData(control: FormControl, allowEmptyString: boolean, controlKey?: string): unknown {
    if (control.valid || (control.errors && Object.keys(control.errors).length === 1 && control.errors.required)) {
      const value = control.value;
      const isValueEmpty = controlKey === 'name' || !allowEmptyString ? this.isEmpty(value, { emptyStringIsEmpty: true }) : this.isEmpty(value);
      return isValueEmpty ? undefined : value;
    }
    return undefined;
  }

  private extractFormGroupData(group: FormGroup, allowEmptyString: boolean): unknown {
    const result = Object.keys(group.controls).reduce((acc, key) => {
      const childData = this.extractSavableData(group.controls[key], allowEmptyString, key);
      if (childData !== undefined) {
        acc[key] = childData;
      }
      return acc;
    }, {} as unknown);
    return Object.keys(result).length ? result : undefined;
  }

  private extractFormArrayData(array: FormArray, allowEmptyString: boolean): unknown {
    const result = array.controls
      .map(ctrl => this.extractSavableData(ctrl, allowEmptyString))
      .filter(item => item?.id || item?.tempUuid);
    return result;
  }

  private mergePayloads(base: SubmissionFormData, incoming: SubmissionFormData): SubmissionFormData {
    return mergeWith({}, base, incoming, this.customMerge.bind(this));
  }

  private customMerge(objValue: any, srcValue: any): any {
    if (!Array.isArray(objValue)) {
      return undefined;
    }

    if (!Array.isArray(srcValue)) {
      return [];
    }

    return srcValue.map(newItem => {
      const oldItem = this.findMatchingItem(objValue, newItem);
      if (oldItem) {
        return mergeWith({}, oldItem, newItem, this.customMerge.bind(this));
      }
      return newItem;
    });
  }

  private findMatchingItem(array: any[], newItem: any): any {
    if (!newItem) {
      return undefined;
    }

    if (newItem.id != null) {
      return array.find(oldItem => oldItem?.id === newItem?.id);
    }

    if (newItem.tempUuid) {
      return array.find(oldItem => oldItem?.tempUuid === newItem?.tempUuid);
    }
    return undefined;
  }

  private isEqual(payload1: unknown, payload2: unknown): boolean {
    return CommonHelper.isEqual(payload1, payload2);
  }

  private getOwnersDiff(
    currentOwners: OwnerInformationFormData[],
    lastSavedOwners: OwnerInformationFormData[]
  ): OwnersDiffData {
    const currentMap = new Map<string, OwnerInformationFormData>();
    const lastSavedMap = new Map<string, OwnerInformationFormData>();

    const getKey = (owner: OwnerInformationFormData): string => {
      if (owner.id) {
        return `id-${owner.id}`;
      }
      return `temp-${owner.tempUuid}`;
    };

    currentOwners.forEach(owner => {
      currentMap.set(getKey(owner), owner);
    });
    lastSavedOwners.forEach(owner => {
      lastSavedMap.set(getKey(owner), owner);
    });

    const addedOrUpdated: OwnerInformationFormData[] = [];
    currentMap.forEach((owner, key) => {
      const oldOwner = lastSavedMap.get(key);
      if (!oldOwner || !this.isEqual(oldOwner, owner)) {
        addedOrUpdated.push(owner);
      }
    });

    const removed: OwnerInformationFormData[] = [];
    lastSavedMap.forEach((owner, key) => {
      if (!currentMap.has(key)) {
        removed.push(owner);
      }
    });

    return { addedOrUpdated, removed };
  }

  private isEmpty(value: unknown, options?: DeepIsEmptyOptions): boolean {
    return CommonHelper.deepIsEmpty(value, options);
  }
}
